import { getBrowserMeasurementContext } from './measurement-context';
import { getCachedAnalyticsIdentity } from './analytics-identity';
import { useSyncExternalStore } from 'react';
import * as SecureStore from 'expo-secure-store';
import { Linking, Platform } from 'react-native';
import type { APIResponse } from '@tabbio/types';
import { getAPIBaseURL, getAuthAPIBaseURL } from './api-url';
import { getAppScheme } from './app-scheme';
import { addSentryBreadcrumb, captureSentryException } from './sentry';
import { markStartupMetric } from './startup-metrics';
import {
  readWebStorage,
  removeWebStorage,
  writeWebStorage,
} from './web-storage';
import { clearNotificationBadge } from './notification-count';
import { clearStoredPushToken, getStoredPushToken } from './push-token-storage';
import { clearPostAuthTarget } from './auth-handoff';
import { clearStoredResumeHandoff } from './resume-handoff-storage';

const API_URL = getAPIBaseURL();
const AUTH_API_URL = getAuthAPIBaseURL();
const APP_REFRESH_TOKEN_STORAGE_KEY = 'tabbio_app_refresh_token';
const CACHED_AUTH_USER_STORAGE_KEY = 'tabbio_cached_auth_user';
const LEGACY_COOKIE_STORAGE_KEY = 'tabbio_cookie';
const LEGACY_BEARER_TOKEN_STORAGE_KEY = 'tabbio_bearer_token';
const LEGACY_SESSION_CACHE_STORAGE_KEY = 'tabbio_session_data';
const ACCESS_TOKEN_REFRESH_SKEW_MS = 30_000;
const APP_CLIENT_PLATFORM_HEADER = 'x-tabbio-client-platform';
const APP_DEVICE_LABEL_HEADER = 'x-tabbio-device-label';
const AUTH_BOOTSTRAP_TIMEOUT_MS = 8_000;
const AUTH_BACKGROUND_REFRESH_TIMEOUT_MS = 12_000;
const LOGOUT_REQUEST_TIMEOUT_MS = 5_000;

export interface AppAuthUser {
  id: string;
  email: string;
  name: string;
  image: string | null;
  username?: string | null;
  status: string;
  isOnboardingCompleted: boolean;
  isUaePassVerified: boolean;
  role?: string | null;
  locale?: string | null;
  phone?: string | null;
}

export interface AppAuthSessionPayload {
  accessToken: string;
  accessTokenExpiresAt: string;
  refreshToken?: string;
  user: AppAuthUser;
  impersonation?: AppAuthImpersonation;
  isPartner?: boolean;
}

export interface AppAuthImpersonation {
  sessionId: string;
  actorName: string;
  actorEmail: string;
  targetUserId: string;
  expiresAt: string;
  viewOnly: true;
}

export type AppAuthTokenStatus =
  | 'unknown'
  | 'refreshing'
  | 'ready'
  | 'unavailable';

export interface AppAuthState {
  status: 'loading' | 'authenticated' | 'anonymous';
  isBootstrapped: boolean;
  user: AppAuthUser | null;
  accessToken: string | null;
  accessTokenExpiresAt: string | null;
  tokenStatus: AppAuthTokenStatus;
  impersonation?: AppAuthImpersonation | null;
}

export class AppAuthRequiredError extends Error {
  readonly code = 'AUTHENTICATION_REQUIRED';

  constructor(message = 'Authentication required') {
    super(message);
    this.name = 'AppAuthRequiredError';
  }
}

type AppFetchOptions = {
  requiresAuth?: boolean;
  retryOnUnauthorized?: boolean;
  includeBetterAuthCookie?: boolean;
};

const listeners = new Set<() => void>();

let authState: AppAuthState = {
  status: 'loading',
  isBootstrapped: false,
  user: null,
  accessToken: null,
  accessTokenExpiresAt: null,
  tokenStatus: 'unknown',
  impersonation: null,
};

let bootstrapPromise: Promise<void> | null = null;
let refreshPromise: Promise<AppAuthSessionPayload | null> | null = null;
let eagerBootstrapStarted = false;
let nativeRefreshRetryHandle: ReturnType<typeof setTimeout> | null = null;

function notifyListeners() {
  listeners.forEach((listener) => listener());
}

function setAuthState(nextState: AppAuthState) {
  authState = nextState;
  notifyListeners();
}

async function readStorage(key: string) {
  if (Platform.OS === 'web') {
    return readWebStorage(key);
  }

  return SecureStore.getItemAsync(key);
}

async function writeStorage(key: string, value: string) {
  if (Platform.OS === 'web') {
    writeWebStorage(key, value);
    return;
  }

  await SecureStore.setItemAsync(key, value);
}

async function removeStorage(key: string) {
  if (Platform.OS === 'web') {
    removeWebStorage(key);
    return;
  }

  await SecureStore.deleteItemAsync(key);
}

function getDeviceLabel() {
  return Platform.OS === 'web' ? 'Expo Web' : 'Expo App';
}

function buildHeaders(headersInit?: HeadersInit) {
  const headers = new Headers(headersInit ?? {});
  const measurement = getBrowserMeasurementContext();
  if (measurement) headers.set('x-tabbio-measurement-context', measurement);
  else headers.delete('x-tabbio-measurement-context');
  const analyticsIdentity = getCachedAnalyticsIdentity();
  if (analyticsIdentity)
    headers.set('x-tabbio-analytics-instance', analyticsIdentity.appInstanceId);
  else headers.delete('x-tabbio-analytics-instance');

  if (!headers.has(APP_CLIENT_PLATFORM_HEADER)) {
    headers.set(APP_CLIENT_PLATFORM_HEADER, Platform.OS);
  }

  if (!headers.has(APP_DEVICE_LABEL_HEADER)) {
    headers.set(APP_DEVICE_LABEL_HEADER, getDeviceLabel());
  }

  return headers;
}

function isAccessTokenFresh(expiresAt?: string | null) {
  if (!expiresAt) {
    return false;
  }

  const expiresAtMs = new Date(expiresAt).getTime();
  if (Number.isNaN(expiresAtMs)) {
    return false;
  }

  return expiresAtMs - ACCESS_TOKEN_REFRESH_SKEW_MS > Date.now();
}

export function hasUsableAccessToken(state: AppAuthState = authState) {
  return (
    state.status === 'authenticated' &&
    state.tokenStatus === 'ready' &&
    !!state.accessToken &&
    !!state.accessTokenExpiresAt
  );
}

export function isAppAuthRequiredError(error: unknown) {
  const code =
    error && typeof error === 'object'
      ? (error as { code?: unknown }).code
      : null;
  const message = error instanceof Error ? error.message.toLowerCase() : '';

  return (
    error instanceof AppAuthRequiredError ||
    (error instanceof Error &&
      (message.includes('authentication required') ||
        message.includes('sign in to manage your subscription') ||
        code === 'AUTHENTICATION_REQUIRED' ||
        (code === 'UNAUTHORIZED' && message.includes('auth'))))
  );
}

function parseLegacyCookieHeader(raw: string | null) {
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Record<
      string,
      { value: string; expires: string | null }
    >;

    const header = Object.entries(parsed)
      .filter(
        ([, value]) => !value.expires || new Date(value.expires) > new Date(),
      )
      .map(([key, value]) => `${key}=${value.value}`)
      .join('; ');

    return header || null;
  } catch {
    return null;
  }
}

async function getStoredRefreshToken() {
  if (Platform.OS === 'web') {
    return null;
  }

  const token = (await readStorage(APP_REFRESH_TOKEN_STORAGE_KEY))?.trim();
  return token ? token : null;
}

async function persistRefreshToken(refreshToken?: string) {
  if (Platform.OS === 'web') {
    return;
  }

  const normalized = refreshToken?.trim();
  if (!normalized) {
    await removeStorage(APP_REFRESH_TOKEN_STORAGE_KEY);
    return;
  }

  await writeStorage(APP_REFRESH_TOKEN_STORAGE_KEY, normalized);
}

async function persistCachedAuthUser(user?: AppAuthUser | null) {
  if (Platform.OS === 'web') {
    return;
  }

  if (!user) {
    await removeStorage(CACHED_AUTH_USER_STORAGE_KEY);
    return;
  }

  await writeStorage(CACHED_AUTH_USER_STORAGE_KEY, JSON.stringify(user));
}

async function readCachedAuthUser() {
  if (Platform.OS === 'web') {
    return null;
  }

  const raw = await readStorage(CACHED_AUTH_USER_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    return normalizeAuthUser(JSON.parse(raw));
  } catch {
    await persistCachedAuthUser(null);
    return null;
  }
}

function normalizeAuthUser(input: unknown): AppAuthUser {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    throw new Error('Authentication response is missing user data');
  }

  const user = input as Record<string, unknown>;

  return {
    id: String(user.id ?? ''),
    email: String(user.email ?? ''),
    name: String(user.name ?? ''),
    image: typeof user.image === 'string' ? user.image : null,
    username: typeof user.username === 'string' ? user.username : null,
    status: typeof user.status === 'string' ? user.status : 'active',
    isOnboardingCompleted: Boolean(user.isOnboardingCompleted),
    isUaePassVerified: Boolean(user.isUaePassVerified),
    role: typeof user.role === 'string' ? user.role : null,
    locale: typeof user.locale === 'string' ? user.locale : null,
    phone: typeof user.phone === 'string' ? user.phone : null,
  };
}

function getErrorMessage(
  payload: APIResponse<unknown> | null,
  fallback: string,
) {
  const message = payload?.error?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
}

async function parseApiResponse<T>(
  response: Response,
): Promise<APIResponse<T> | null> {
  try {
    return (await response.json()) as APIResponse<T>;
  } catch {
    return null;
  }
}

export function isTransientAuthNetworkError(error: unknown) {
  if (!(error instanceof Error)) {
    return false;
  }

  if (error.name === 'AbortError') {
    return true;
  }

  const message = error.message.toLowerCase();
  return (
    error.name === 'TypeError' &&
    (message.includes('network request failed') ||
      message.includes('failed to fetch') ||
      message.includes('load failed') ||
      message.includes('networkerror') ||
      message.includes('fetch failed'))
  );
}

function clearNativeRefreshRetry() {
  if (!nativeRefreshRetryHandle) {
    return;
  }

  clearTimeout(nativeRefreshRetryHandle);
  nativeRefreshRetryHandle = null;
}

function scheduleNativeRefreshRetry() {
  if (Platform.OS === 'web' || nativeRefreshRetryHandle) {
    return;
  }

  nativeRefreshRetryHandle = setTimeout(() => {
    nativeRefreshRetryHandle = null;
    void initializeAppAuth().catch(() => undefined);
  }, 5_000);
}

async function setAnonymousState() {
  clearNativeRefreshRetry();
  await Promise.all([
    persistRefreshToken(undefined),
    persistCachedAuthUser(null),
  ]);
  setAuthState({
    status: 'anonymous',
    isBootstrapped: true,
    user: null,
    accessToken: null,
    accessTokenExpiresAt: null,
    tokenStatus: 'unavailable',
    impersonation: null,
  });
}

function setLoadingState(tokenStatus: AppAuthTokenStatus = 'unknown') {
  setAuthState({
    ...authState,
    status: authState.isBootstrapped ? authState.status : 'loading',
    tokenStatus,
  });
}

export function clearLegacyBetterAuthState() {
  void removeStorage(LEGACY_COOKIE_STORAGE_KEY);
  void removeStorage(LEGACY_BEARER_TOKEN_STORAGE_KEY);
  void removeStorage(LEGACY_SESSION_CACHE_STORAGE_KEY);
}

export async function getStoredBetterAuthCookieHeader() {
  if (Platform.OS === 'web') {
    return null;
  }

  return parseLegacyCookieHeader(await readStorage(LEGACY_COOKIE_STORAGE_KEY));
}

export function getAuthStateSnapshot() {
  return authState;
}

export function useAppAuthState() {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => authState,
    () => authState,
  );
}

export function getCurrentAccessToken() {
  return authState.accessToken;
}

export async function applyAppAuthSession(payload: AppAuthSessionPayload) {
  const normalizedUser = normalizeAuthUser(payload.user);
  const previousUser = authState.user;
  const nextUser = {
    ...normalizedUser,
    role: normalizedUser.role ?? previousUser?.role ?? null,
    locale: normalizedUser.locale ?? previousUser?.locale ?? null,
    phone: normalizedUser.phone ?? previousUser?.phone ?? null,
  };

  clearNativeRefreshRetry();

  const storageWrites = payload.impersonation
    ? []
    : [persistCachedAuthUser(nextUser)];
  if (Object.prototype.hasOwnProperty.call(payload, 'refreshToken')) {
    storageWrites.push(persistRefreshToken(payload.refreshToken));
  }

  await Promise.all(storageWrites);
  setAuthState({
    status: 'authenticated',
    isBootstrapped: true,
    user: nextUser,
    accessToken: payload.accessToken,
    accessTokenExpiresAt: payload.accessTokenExpiresAt,
    tokenStatus: 'ready',
    impersonation: payload.impersonation ?? null,
  });
}

export function updateAuthenticatedUser(user: unknown) {
  if (authState.status !== 'authenticated') {
    return;
  }

  const nextUser = normalizeAuthUser(user);
  void persistCachedAuthUser(nextUser);
  setAuthState({
    ...authState,
    user: nextUser,
  });
}

export function mergeAuthenticatedUser(patch: Partial<AppAuthUser>) {
  if (authState.status !== 'authenticated' || !authState.user) {
    return;
  }

  const nextUser = {
    ...authState.user,
    ...patch,
  };

  void persistCachedAuthUser(nextUser);
  setAuthState({
    ...authState,
    user: nextUser,
  });
}

function getCurrentSessionPayload(): AppAuthSessionPayload | null {
  if (
    authState.status !== 'authenticated' ||
    !authState.accessToken ||
    !authState.accessTokenExpiresAt ||
    !authState.user
  ) {
    return null;
  }

  return {
    accessToken: authState.accessToken,
    accessTokenExpiresAt: authState.accessTokenExpiresAt,
    user: authState.user,
    ...(authState.impersonation
      ? { impersonation: authState.impersonation }
      : {}),
  };
}

export async function endAppImpersonation() {
  if (!authState.impersonation) return getCurrentSessionPayload();
  return refreshAuthenticatedSession({ force: true, allowAnonymous: true });
}

async function requestSessionPayload(
  path: string,
  body: Record<string, unknown>,
  options: {
    includeBetterAuthCookie?: boolean;
    allowAnonymous?: boolean;
    timeoutMs?: number;
  } = {},
) {
  const headers = buildHeaders({
    'Content-Type': 'application/json',
  });

  if (options.includeBetterAuthCookie) {
    const cookieHeader = await getStoredBetterAuthCookieHeader();
    if (cookieHeader) {
      headers.set('cookie', cookieHeader);
    }
  }

  const controller =
    options.timeoutMs && typeof AbortController !== 'undefined'
      ? new AbortController()
      : null;
  const timeoutHandle =
    controller && options.timeoutMs
      ? setTimeout(() => controller.abort(), options.timeoutMs)
      : null;

  addSentryBreadcrumb('auth: requestSessionPayload', {
    url: path,
    method: 'POST',
    includeCookie: options.includeBetterAuthCookie ?? false,
    allowAnonymous: options.allowAnonymous ?? false,
  });

  let response: Response;
  try {
    response = await fetch(`${AUTH_API_URL}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
      signal: controller?.signal,
    });
  } catch (error) {
    if (options.allowAnonymous && isTransientAuthNetworkError(error)) {
      // Never demote an already-authenticated session on a transient network
      // failure (backgrounded tab waking up, sleep/wake, flaky connection,
      // refresh timeout). Keep the session; the next API call retries the
      // refresh once the network is back. Only unauthenticated bootstrap may
      // settle to anonymous here.
      if (authState.status !== 'authenticated') {
        await setAnonymousState();
      }
      return null;
    }

    captureSentryException(error, {
      scope: 'auth_request_session',
      url: path,
      method: 'POST',
    });
    throw error;
  } finally {
    if (timeoutHandle) {
      clearTimeout(timeoutHandle);
    }
  }

  const payload = await parseApiResponse<AppAuthSessionPayload>(response);

  if (!response.ok || payload?.error || !payload?.data) {
    const errorCode = payload?.error?.code;
    if (
      options.allowAnonymous &&
      (response.status === 401 ||
        response.status === 403 ||
        errorCode === 'UNAUTHORIZED' ||
        errorCode === 'SESSION_REUSE_DETECTED')
    ) {
      await setAnonymousState();
      return null;
    }

    throw new Error(getErrorMessage(payload, 'Authentication request failed'));
  }

  await applyAppAuthSession({
    ...payload.data,
    user: normalizeAuthUser(payload.data.user),
  });
  clearLegacyBetterAuthState();
  return payload.data;
}

export async function exchangeAppAuthSession() {
  return requestSessionPayload(
    '/api/auth/tokens/exchange',
    {},
    { includeBetterAuthCookie: Platform.OS !== 'web' },
  );
}

export async function refreshAuthenticatedSession(
  options: {
    force?: boolean;
    allowAnonymous?: boolean;
    timeoutMs?: number;
  } = {},
) {
  if (!options.force) {
    const currentSession = getCurrentSessionPayload();
    if (
      currentSession &&
      isAccessTokenFresh(currentSession.accessTokenExpiresAt)
    ) {
      return currentSession;
    }
  }

  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const refreshToken = await getStoredRefreshToken();

    if (Platform.OS !== 'web' && !refreshToken) {
      if (options.allowAnonymous) {
        await setAnonymousState();
        return null;
      }

      throw new AppAuthRequiredError();
    }

    try {
      return await requestSessionPayload(
        '/api/auth/tokens/refresh',
        Platform.OS === 'web' ? {} : { refreshToken },
        {
          allowAnonymous: options.allowAnonymous,
          timeoutMs: options.timeoutMs,
        },
      );
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export async function initializeAppAuth() {
  if (authState.isBootstrapped) {
    markStartupMetric('auth_bootstrap_ms');
    return;
  }

  if (bootstrapPromise) {
    return bootstrapPromise;
  }

  setLoadingState('unknown');

  bootstrapPromise = (async () => {
    const refreshToken = await getStoredRefreshToken();
    const cachedUser = refreshToken ? await readCachedAuthUser() : null;

    if (Platform.OS !== 'web' && refreshToken && cachedUser) {
      setAuthState({
        status: 'loading',
        isBootstrapped: false,
        user: cachedUser,
        accessToken: null,
        accessTokenExpiresAt: null,
        tokenStatus: 'refreshing',
      });
      try {
        await refreshAuthenticatedSession({
          force: true,
          allowAnonymous: true,
          timeoutMs: AUTH_BACKGROUND_REFRESH_TIMEOUT_MS,
        });
      } catch {
        scheduleNativeRefreshRetry();
      }

      if (authState.status !== 'anonymous' && !hasUsableAccessToken()) {
        setAuthState({
          status: 'loading',
          isBootstrapped: false,
          user: cachedUser,
          accessToken: null,
          accessTokenExpiresAt: null,
          tokenStatus: 'refreshing',
        });
      }

      markStartupMetric('auth_bootstrap_ms');
      return;
    }

    if (Platform.OS !== 'web' && !refreshToken) {
      await setAnonymousState();
      markStartupMetric('auth_bootstrap_ms');
      return;
    }

    try {
      await refreshAuthenticatedSession({
        force: true,
        allowAnonymous: true,
        timeoutMs: AUTH_BOOTSTRAP_TIMEOUT_MS,
      });
    } catch {
      // Fall through: state settles below so the splash can hide.
    }

    if (!authState.isBootstrapped) {
      await setAnonymousState();
    }

    markStartupMetric('auth_bootstrap_ms');
  })().finally(() => {
    bootstrapPromise = null;
  });

  return bootstrapPromise;
}

export function startAppAuthBootstrap() {
  if (eagerBootstrapStarted || authState.isBootstrapped) {
    return;
  }

  if (Platform.OS === 'web' && typeof window === 'undefined') {
    return;
  }

  eagerBootstrapStarted = true;

  void initializeAppAuth().catch(() => {
    eagerBootstrapStarted = false;
  });
}

export async function ensureValidAccessToken() {
  if (!authState.isBootstrapped) {
    await initializeAppAuth();
  }

  const currentSession = getCurrentSessionPayload();
  if (
    currentSession &&
    isAccessTokenFresh(currentSession.accessTokenExpiresAt)
  ) {
    return currentSession.accessToken;
  }

  const refreshedSession = await refreshAuthenticatedSession({
    force: true,
    allowAnonymous: true,
  });
  return refreshedSession?.accessToken ?? null;
}

export async function getAuthorizedHeaders(headersInit?: HeadersInit) {
  const accessToken = await ensureValidAccessToken();
  if (!accessToken) {
    throw new AppAuthRequiredError();
  }

  const headers = buildHeaders(headersInit);
  headers.set('Authorization', `Bearer ${accessToken}`);
  return Object.fromEntries(headers.entries());
}

export async function refreshAuthenticatedUser() {
  const response = await appFetch(`${API_URL}/api/users/me`, {
    method: 'GET',
  });
  const payload = await parseApiResponse<Record<string, unknown>>(response);

  if (!response.ok || payload?.error || !payload?.data) {
    throw new Error(getErrorMessage(payload, 'Unable to refresh account'));
  }

  const user = normalizeAuthUser(payload.data);
  updateAuthenticatedUser(user);
  return user;
}

export async function signOutFromApp() {
  // Capture the refresh token before clearing local state so the server
  // logout call still includes it.
  const [refreshToken, pushToken] = await Promise.all([
    getStoredRefreshToken(),
    getStoredPushToken(),
  ]);

  clearLegacyBetterAuthState();

  let logoutUrl: string | undefined;
  try {
    const controller =
      typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutHandle = controller
      ? setTimeout(() => controller.abort(), LOGOUT_REQUEST_TIMEOUT_MS)
      : null;
    const headers = buildHeaders({
      'Content-Type': 'application/json',
    });
    const nativeBody: { refreshToken?: string; pushToken?: string } = {};
    if (refreshToken) {
      nativeBody.refreshToken = refreshToken;
    }
    if (pushToken) {
      nativeBody.pushToken = pushToken;
    }

    try {
      const response = await fetch(`${AUTH_API_URL}/api/auth/tokens/logout`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify(Platform.OS === 'web' ? {} : nativeBody),
        signal: controller?.signal,
      });
      const payload = await parseApiResponse<{
        status: boolean;
        uaePassLogoutUrl?: string;
      }>(response);
      const payloadLogoutUrl = payload?.data?.uaePassLogoutUrl;

      if (typeof payloadLogoutUrl === 'string' && payloadLogoutUrl.trim()) {
        logoutUrl = payloadLogoutUrl;
      }
    } finally {
      if (timeoutHandle) {
        clearTimeout(timeoutHandle);
      }
    }
  } catch {
    // Best effort only. Local sign-out must still complete.
  }

  // Clear local session/cache after the logout request has had a bounded chance
  // to unregister this device's push token while the refresh token is available.
  // The pending website resume upload and the post-auth target belong to the
  // account that is leaving: on iOS the Keychain copy would otherwise outlive
  // this account, the reinstall, and be claimed by whoever signs up next
  // (incident 2026-10-04).
  clearPostAuthTarget();
  await Promise.all([
    clearStoredPushToken(),
    clearStoredResumeHandoff(),
    setAnonymousState(),
  ]);

  // Clear the app-icon badge and dismiss delivered notifications so stale
  // push notifications do not linger after sign-out / account deletion.
  clearNotificationBadge();

  if (logoutUrl) {
    void openUaePassLogoutUrl(logoutUrl);
  }
}

export async function openUaePassLogoutUrl(url: string) {
  if (Platform.OS === 'web') {
    if (typeof window !== 'undefined') {
      window.location.assign(url);
    }
    return;
  }

  const logoutUrl = withNativeUaePassLogoutRedirect(url);

  try {
    const { router } = await import('expo-router');
    router.push({
      pathname: '/uae-pass/logout',
      params: { logoutUrl },
    } as any);
    return;
  } catch {
    // Fall through to the OS as a last-resort escape hatch.
  }

  await Linking.openURL(logoutUrl).catch(() => undefined);
}

function encodeBase64Url(value: string) {
  if (typeof globalThis.btoa === 'function') {
    return globalThis
      .btoa(value)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  const alphabet =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  let encoded = '';
  let index = 0;

  while (index < value.length) {
    const first = value.charCodeAt(index++) & 0xff;
    const second =
      index < value.length ? value.charCodeAt(index++) & 0xff : NaN;
    const third = index < value.length ? value.charCodeAt(index++) & 0xff : NaN;

    encoded += alphabet[first >> 2];
    encoded += alphabet[((first & 3) << 4) | ((second || 0) >> 4)];
    encoded += Number.isNaN(second)
      ? '='
      : alphabet[((second & 15) << 2) | ((third || 0) >> 6)];
    encoded += Number.isNaN(third) ? '=' : alphabet[third & 63];
  }

  return encoded.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function buildNativeUaePassLogoutReturnUrl(encodedState?: string | null) {
  const redirectUri = new URL(`${getAppScheme()}:///uae-pass/logout`);
  const state =
    encodedState ||
    encodeBase64Url(
      new URLSearchParams({
        action: 'logout',
        returnTo: '/',
      }).toString(),
    );

  redirectUri.searchParams.set('state', state);
  return redirectUri.toString();
}

function withNativeUaePassLogoutRedirect(logoutUrl: string) {
  try {
    const parsed = new URL(logoutUrl);
    const currentRedirectUri = parsed.searchParams.get('redirect_uri');
    let encodedState: string | null = null;

    if (currentRedirectUri) {
      try {
        encodedState = new URL(currentRedirectUri).searchParams.get('state');
      } catch {
        encodedState = null;
      }
    }

    parsed.searchParams.set(
      'redirect_uri',
      buildNativeUaePassLogoutReturnUrl(encodedState),
    );
    return parsed.toString();
  } catch {
    return logoutUrl;
  }
}

async function performAppFetch(
  input: RequestInfo | URL,
  init: RequestInit,
  options: Required<AppFetchOptions>,
) {
  const headers = buildHeaders(init.headers);

  if (options.includeBetterAuthCookie) {
    const cookieHeader = await getStoredBetterAuthCookieHeader();
    if (cookieHeader) {
      headers.set('cookie', cookieHeader);
    }
  }

  if (options.requiresAuth) {
    const accessToken = await ensureValidAccessToken();
    if (!accessToken) {
      throw new AppAuthRequiredError();
    }

    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  const response = await fetch(input, {
    ...init,
    headers,
    credentials: init.credentials ?? 'include',
  });

  if (
    options.requiresAuth &&
    options.retryOnUnauthorized &&
    response.status === 401
  ) {
    const refreshedSession = await refreshAuthenticatedSession({
      force: true,
      allowAnonymous: true,
    });

    if (refreshedSession?.accessToken) {
      const retryHeaders = buildHeaders(init.headers);
      retryHeaders.set(
        'Authorization',
        `Bearer ${refreshedSession.accessToken}`,
      );

      if (options.includeBetterAuthCookie) {
        const cookieHeader = await getStoredBetterAuthCookieHeader();
        if (cookieHeader) {
          retryHeaders.set('cookie', cookieHeader);
        }
      }

      return fetch(input, {
        ...init,
        headers: retryHeaders,
        credentials: init.credentials ?? 'include',
      });
    }
  }

  return response;
}

export async function appFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
  options: AppFetchOptions = {},
) {
  return performAppFetch(input, init, {
    requiresAuth: options.requiresAuth !== false,
    retryOnUnauthorized: options.retryOnUnauthorized !== false,
    includeBetterAuthCookie: options.includeBetterAuthCookie === true,
  });
}
